import { useEffect, useRef, useState } from 'react';
import { GripVertical, Plus, Trash2 } from '@ui/shared/icons';
import { formatShortDate } from '@ui/shared/format/date';
import { DatePickerPopover } from '@ui/shared/primitives/DatePickerPopover';
import { LifecycleConfirmDialog } from '@ui/shared/primitives/LifecycleFooter';
import { ActionStatusPill, type ActionDisplayStatus } from './ActionStatusPill';
import { EffortBars } from './FormPrimitives';

/**
 * The Actions table (design/screens/objective-create-desktop.md SS6). A
 * flex/grid row list (not a literal <table>, matching every other list in
 * the product) — drag-reorder, per-action effort/duration, per-action target
 * date, the three-state status pill, delete with confirm, an inline
 * quick-add row, and the footer summary line.
 *
 * All math (total effort, done/total counts) is display-only summation over
 * already-derived per-action fields the caller supplies — no domain math
 * lives here (dev-ui rule): the caller is responsible for feeding
 * `effortMinutes`/`status` from the real ActionView/ObjectiveDraft shape.
 */
export interface ActionRowData {
  readonly actionId: string;
  readonly title: string;
  readonly dueDate: string | null;
  readonly status: ActionDisplayStatus;
  readonly effortLevel: number | null; // 1-3, per SS6.1's narrower per-action scale
  readonly effortMinutes: number | null;
  /** True right after a to_do/doing -> done transition, for the modest
   *  check-pop beat (SS6.2); caller clears this after a short delay. */
  readonly justCompleted?: boolean;
}

export function ActionsTable({
  today,
  rows,
  onReorder,
  onRename,
  onSetDueDate,
  onSetEffort,
  onSetStatus,
  onRemove,
  onAdd,
}: {
  readonly today: string;
  readonly rows: readonly ActionRowData[];
  readonly onReorder: (orderedActionIds: readonly string[]) => void;
  readonly onRename: (actionId: string, title: string) => void;
  readonly onSetDueDate: (actionId: string, dueDate: string | null) => void;
  readonly onSetEffort: (actionId: string, effortLevel: number | null, effortMinutes: number | null) => void;
  readonly onSetStatus: (actionId: string, status: ActionDisplayStatus) => void;
  readonly onRemove: (actionId: string) => void;
  readonly onAdd: (title: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [quickAddValue, setQuickAddValue] = useState('');

  const totalEffortMinutes = rows.reduce((sum, r) => sum + (r.effortMinutes ?? 0), 0);
  const doneCount = rows.filter((r) => r.status === 'done').length;

  function moveByKeyboard(actionId: string, direction: -1 | 1) {
    const index = rows.findIndex((r) => r.actionId === actionId);
    if (index < 0) return;
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const next = rows.map((r) => r.actionId);
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved as string);
    onReorder(next);
  }

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const ids = rows.map((r) => r.actionId);
    const fromIndex = ids.indexOf(dragId);
    const toIndex = ids.indexOf(targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDragId(null);
      return;
    }
    const next = [...ids];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved as string);
    onReorder(next);
    setDragId(null);
  }

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        overflow: 'visible',
      }}
    >
      {rows.length === 0 && (
        <p style={{ padding: 'var(--space-4) var(--space-4) 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          No actions yet. Add the first concrete step below.
        </p>
      )}

      {rows.map((row, index) => (
        <div key={row.actionId}>
          <ActionRow
            today={today}
            row={row}
            ordinal={index + 1}
            draggable
            onDragStart={() => setDragId(row.actionId)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(row.actionId)}
            onMoveUp={() => moveByKeyboard(row.actionId, -1)}
            onMoveDown={() => moveByKeyboard(row.actionId, 1)}
            onRename={(title) => onRename(row.actionId, title)}
            onSetDueDate={(date) => onSetDueDate(row.actionId, date)}
            onSetEffort={(level, minutes) => onSetEffort(row.actionId, level, minutes)}
            onSetStatus={(status) => onSetStatus(row.actionId, status)}
            onDeleteRequested={() => setPendingDelete(row.actionId)}
          />
          {index < rows.length - 1 && <div style={{ borderTop: '1px solid var(--border-subtle)' }} />}
          {pendingDelete === row.actionId && (
            <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
              <LifecycleConfirmDialog
                ariaLabel={`Remove ${row.title || 'this action'}`}
                message="Remove this action? It won't count toward progress or the plan."
                confirmLabel="Remove"
                onConfirm={() => {
                  onRemove(row.actionId);
                  setPendingDelete(null);
                }}
                onCancel={() => setPendingDelete(null)}
              />
            </div>
          )}
        </div>
      ))}

      {rows.length > 0 && <div style={{ borderTop: '1px solid var(--border-subtle)' }} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)' }}>
        <Plus size={16} color="var(--text-muted)" aria-hidden="true" />
        <input
          value={quickAddValue}
          onChange={(e) => setQuickAddValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && quickAddValue.trim().length > 0) {
              onAdd(quickAddValue.trim());
              setQuickAddValue('');
            }
          }}
          placeholder="Add an action..."
          aria-label="Add an action"
          style={{
            flex: 1,
            minHeight: 'var(--touch-target-min)',
            background: 'transparent',
            color: 'var(--text-primary)',
            fontSize: 'var(--type-body-size)',
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-3) var(--space-4)',
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Total effort · ~{totalEffortMinutes}m / cycle
        </span>
        <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          {rows.length} actions · {doneCount} done
        </span>
      </div>
    </div>
  );
}

function ActionRow({
  today,
  row,
  ordinal,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onMoveUp,
  onMoveDown,
  onRename,
  onSetDueDate,
  onSetEffort,
  onSetStatus,
  onDeleteRequested,
}: {
  readonly today: string;
  readonly row: ActionRowData;
  readonly ordinal: number;
  readonly draggable: boolean;
  readonly onDragStart: () => void;
  readonly onDragOver: (e: React.DragEvent) => void;
  readonly onDrop: () => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
  readonly onRename: (title: string) => void;
  readonly onSetDueDate: (date: string | null) => void;
  readonly onSetEffort: (level: number | null, minutes: number | null) => void;
  readonly onSetStatus: (status: ActionDisplayStatus) => void;
  readonly onDeleteRequested: () => void;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(row.title);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [effortPopoverOpen, setEffortPopoverOpen] = useState(false);
  const [picking, setPicking] = useState(false); // keyboard drag "picked up" state

  useEffect(() => setTitleDraft(row.title), [row.title]);

  function handleHandleKeyDown(e: React.KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setPicking((p) => !p);
      return;
    }
    if (!picking) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      onMoveUp();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onMoveDown();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setPicking(false);
    }
  }

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`action-row-${row.actionId}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <button
        type="button"
        aria-label={`Reorder ${row.title || 'action'}${picking ? ' (picked up — use arrow keys to move, Space to drop)' : ''}`}
        aria-pressed={picking}
        onKeyDown={handleHandleKeyDown}
        style={{
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: picking ? 'var(--hue-primary)' : 'var(--text-muted)',
          cursor: 'grab',
          flex: 'none',
        }}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>

      <span className="num" style={{ width: '24px', flex: 'none', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        {String(ordinal).padStart(2, '0')}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        {editingTitle ? (
          <input
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={() => {
              setEditingTitle(false);
              if (titleDraft.trim().length > 0) onRename(titleDraft.trim());
              else setTitleDraft(row.title);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                setTitleDraft(row.title);
                setEditingTitle(false);
              }
            }}
            style={{
              width: '100%',
              minHeight: 'var(--touch-target-min)',
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '0 var(--space-2)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingTitle(true)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              minHeight: 'var(--touch-target-min)',
              fontSize: 'var(--type-body-size)',
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.title || 'Untitled action'}
          </button>
        )}
      </div>

      <div style={{ position: 'relative', flex: 'none' }}>
        <button
          type="button"
          aria-label={`Effort: ${row.effortLevel ?? 'unset'} of 3${row.effortMinutes ? `, ${row.effortMinutes} minutes` : ''}`}
          onClick={() => setEffortPopoverOpen((v) => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minHeight: 'var(--touch-target-min)', padding: '0 var(--space-2)' }}
        >
          <EffortBars filled={row.effortLevel ?? 0} total={3} />
          <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
            {row.effortMinutes ? `${row.effortMinutes}m` : '—'}
          </span>
        </button>
        {effortPopoverOpen && (
          <EffortPopover
            level={row.effortLevel}
            minutes={row.effortMinutes}
            onApply={(level, minutes) => {
              onSetEffort(level, minutes);
              setEffortPopoverOpen(false);
            }}
            onClose={() => setEffortPopoverOpen(false)}
          />
        )}
      </div>

      <div style={{ position: 'relative', flex: 'none' }}>
        <button
          type="button"
          aria-label={row.dueDate ? `Due date: ${formatShortDate(row.dueDate)}` : 'Set due date'}
          onClick={() => setDatePopoverOpen(true)}
          className="num"
          style={{ minHeight: 'var(--touch-target-min)', padding: '0 var(--space-2)', fontSize: 'var(--type-caption-size)', color: row.dueDate ? 'var(--text-secondary)' : 'var(--text-muted)' }}
        >
          {row.dueDate ? formatShortDate(row.dueDate) : '—'}
        </button>
        {datePopoverOpen && (
          <DatePickerPopover
            selected={row.dueDate}
            today={today}
            onSelect={(date) => {
              onSetDueDate(date);
              setDatePopoverOpen(false);
            }}
            onClose={() => setDatePopoverOpen(false)}
          />
        )}
      </div>

      <div style={{ flex: 'none' }}>
        <ActionStatusPill status={row.status} onChange={onSetStatus} justCompleted={row.justCompleted} />
      </div>

      <button
        type="button"
        aria-label={`Delete ${row.title || 'action'}`}
        onClick={onDeleteRequested}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          flex: 'none',
        }}
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

function EffortPopover({
  level,
  minutes,
  onApply,
  onClose,
}: {
  readonly level: number | null;
  readonly minutes: number | null;
  readonly onApply: (level: number | null, minutes: number | null) => void;
  readonly onClose: () => void;
}) {
  const [localLevel, setLocalLevel] = useState(level);
  const [localMinutes, setLocalMinutes] = useState(minutes ?? 0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Set action effort"
      style={{
        position: 'absolute',
        zIndex: 41,
        top: 'calc(100% + var(--space-1))',
        right: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        minWidth: '180px',
      }}
    >
      <div role="radiogroup" aria-label="Effort level" style={{ display: 'flex', gap: 'var(--space-1)' }}>
        {[1, 2, 3].map((tap) => (
          <button
            key={tap}
            type="button"
            role="radio"
            aria-checked={localLevel === tap}
            aria-label={`Effort level ${tap} of 3`}
            onClick={() => setLocalLevel(tap)}
            style={{
              flex: 1,
              minHeight: 'var(--touch-target-min)',
              borderRadius: 'var(--radius-sm)',
              border: localLevel === tap ? '1px solid var(--hue-primary-vivid)' : '1px solid var(--border-subtle)',
              background: localLevel === tap ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, var(--surface-raised))' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EffortBars filled={tap} total={3} />
          </button>
        ))}
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>Duration (minutes)</span>
        <input
          type="number"
          min={0}
          value={localMinutes}
          onChange={(e) => setLocalMinutes(Number(e.target.value))}
          style={{
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-subtle)',
            background: 'var(--surface-sunken)',
            color: 'var(--text-primary)',
            fontSize: 'var(--type-body-size)',
          }}
        />
      </label>
      <button
        type="button"
        onClick={() => onApply(localLevel, localMinutes > 0 ? localMinutes : null)}
        style={{
          minHeight: 'var(--touch-target-min)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--hue-primary-vivid)',
          color: 'var(--text-on-accent)',
          fontWeight: 600,
          fontSize: 'var(--type-caption-size)',
        }}
      >
        Apply
      </button>
    </div>
  );
}
